import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SubsidyReportCardState } from './subsidyReportTypes';
import susbsidyReportsApiService from 'src/api/services/susbsidyReportsApiService';
import { AppDispatch, AppGetState, AppState } from 'src/store/store';
import { RcFile } from 'antd/lib/upload';
import { Certificate } from 'crypto-pro';
// @ts-expect-error
import cadesplugin from 'crypto-pro-cadesplugin';
import { SignDecisionCommand, SignReportPackageCommand } from 'src/api/contracts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import fileHelper from 'src/common/helpers/fileHelper';

dayjs.extend(utc);

const initialState: SubsidyReportCardState = {
    status: 'initial',
    requestId: undefined,
    data: null
};

export const loadReportData = createAsyncThunk('subsidy-report/data', async (id: number) => {
    return await susbsidyReportsApiService.loadReportDetails(id);
});

export const sendReportOnSignAc = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/send-on-sign-ac',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.sendReportOnSignAc(reportId);
    }
);

export const disbandReport = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/disband',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.disbandReport(reportId);
    }
);

export const returnToAc = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/return-to-ac',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.returnReportToAc(reportId);
    }
);

export const uploadSubsidyReportDeclaration = createAsyncThunk<void, RcFile, { state: AppState }>(
    'subsidy-report/upload-declaration',
    async (file: RcFile, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.uploadDeclaration(reportId, file);
    }
);

export const deleteSubsidyReportDeclaration = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/delete-declaration',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.deleteDeclaration(reportId);
    }
);

export const uploadSubsidyReportQuarterlyReport = createAsyncThunk<void, RcFile, { state: AppState }>(
    'subsidy-report/upload-quarterly-report',
    async (file: RcFile, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.uploadQuarterlyReport(reportId, file);
    }
);

export const deleteSubsidyReportQuarterlyReport = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/delete-quarterly-report',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.deleteQuarterlyReport(reportId);
    }
);

export const uploadSubsidyReportPaymentDecision = createAsyncThunk<void, RcFile, { state: AppState }>(
    'subsidy-report/upload-payment-decision',
    async (file: RcFile, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.uploadPaymentDecision(reportId, file);
    }
);

export const deleteSubsidyReportUploadedPaymentDecision = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/delete-payment-decision',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.deleteUploadedPaymentDecision(reportId);
    }
);

export const uploadSubsidyReportReturnDecision = createAsyncThunk<void, RcFile, { state: AppState }>(
    'subsidy-report/upload-return-decision',
    async (file: RcFile, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.uploadReturnDecision(reportId, file);
    }
);

export const deleteSubsidyReportUploadedReturnDecision = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/delete-return-decision',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.deleteUploadedReturnDecision(reportId);
    }
);

export const uploadSubsidyReportReturnLetter = createAsyncThunk<void, RcFile, { state: AppState }>(
    'subsidy-report/upload-return-letter',
    async (file: RcFile, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.uploadReturnLetter(reportId, file);
    }
);

export const agreeReport = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/agree',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.agreeReport(reportId);
    }
);

export const acceptReportToPayment = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/accept-to-payment',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.acceptReportToPayment(reportId);
    }
);

export const signSubsidyReport = (certificate: Certificate) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const state = getState();
    const reportId = selectSubsidyReportId(state);
    if (!reportId) {
        throw new Error('Не задан идентификатор');
    }
    dispatch(startLoading());
    try {
        const cadesApi = await cadesplugin();
        const ownerInfos = await certificate.getOwnerInfo();
        if (!ownerInfos.length) {
            throw new Error('Не удалось получить информацию о владельце сертификата');
        }
        const ownerInfo = ownerInfos[0];
        const algorithmInfo = await certificate.getAlgorithm();
        const request: SignReportPackageCommand = {
            reportId,
            certificateInfo: {
                owner: ownerInfo.description,
                cryptoAlgorithm: `${algorithmInfo.algorithm}:${algorithmInfo.oid}`
            },
            fileSigns: []
        };
        const files = await susbsidyReportsApiService.loadFilesToSign(reportId);
        for (const file of files) {
            const base64 = await file.async('base64');
            const signature = await cadesApi.signBase64(certificate.thumbprint, base64);
            request.fileSigns.push({
                fileName: file.name,
                signature,
                signDatetime: dayjs().utc().toISOString()
            });
        }
        await susbsidyReportsApiService.signReportPackage(request);
        dispatch(endLoading());
    } catch (error) {
        dispatch(endLoading());
        throw error;
    }
};

export const rejectReportByTreasury = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/reject-by-treasury',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.rejectReportByTreasury(reportId);
    }
);

export const signSubsidyReportFormedDecision =
    (certificate: Certificate) => async (dispatch: AppDispatch, getState: AppGetState) => {
        const report = selectSubsidyReport(getState());
        if (!report) {
            throw new Error('Не задан идентификатор');
        }
        if (!report.formedPaymentDecisionDatetime && !report.formedReturnDecisionDatetime) {
            throw new Error('Не найдено подписываемое решение');
        }
        dispatch(startLoading());
        try {
            const cadesApi = await cadesplugin();
            const ownerInfos = await certificate.getOwnerInfo();
            if (!ownerInfos.length) {
                throw new Error('Не удалось получить информацию о владельце сертификата');
            }
            const ownerInfo = ownerInfos[0];
            const algorithmInfo = await certificate.getAlgorithm();
            const file = await (report.formedPaymentDecisionDatetime
                ? susbsidyReportsApiService.downloadFormedPaymentDecision(report.reportId)
                : susbsidyReportsApiService.downloadFormedReturnDecision(report.reportId));
            const base64 = await fileHelper.blobToBase64(file);
            const signature = await cadesApi.signBase64(certificate.thumbprint, base64);
            const request: SignDecisionCommand = {
                reportId: report.reportId,
                signature,
                signDatetime: dayjs().utc().toISOString(),
                certificateInfo: {
                    owner: ownerInfo.description,
                    cryptoAlgorithm: `${algorithmInfo.algorithm}:${algorithmInfo.oid}`
                }
            };
            await susbsidyReportsApiService.signReportDecision(request);
            dispatch(endLoading());
        } catch (error) {
            dispatch(endLoading());
            throw error;
        }
    };

export const rerunSubsidyReport = createAsyncThunk<void, void, { state: AppState }>(
    'subsidy-report/rerun',
    async (_, { getState }) => {
        const reportId = selectSubsidyReportId(getState());
        if (!reportId) {
            throw new Error('Не задан идентификатор');
        }
        return await susbsidyReportsApiService.rerunReport(reportId);
    }
);

export const refreshSubsidyReportData = () => (dispatch: AppDispatch, getState: AppGetState) => {
    const reportId = selectSubsidyReportId(getState());
    if (!reportId) {
        throw new Error('Не задан идентификатор');
    }
    return dispatch(loadReportData(reportId));
};

const slice = createSlice({
    name: 'subsidy-report',
    initialState,
    reducers: {
        reset: () => initialState,
        startLoading: (state) => {
            state.status = 'loading';
        },
        endLoading: (state) => {
            state.status = 'idle';
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loadReportData.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(loadReportData.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.data = {
                        ...action.payload,
                        limits: action.payload.subsidyAmounts
                            ? {
                                  amount1: action.payload.subsidyAmounts[0],
                                  amount2: action.payload.subsidyAmounts[1],
                                  amount3: action.payload.subsidyAmounts[2],
                                  amount4: action.payload.subsidyAmounts[3],
                                  amount5: action.payload.subsidyAmounts[4]
                              }
                            : null,
                        confirmations: action.payload.confirmations.map((dto, index) => ({ ...dto, key: index }))
                    };
                }
            })
            .addCase(loadReportData.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.data = null;
                }
            })
            .addCase(sendReportOnSignAc.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(sendReportOnSignAc.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(sendReportOnSignAc.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportDeclaration.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(uploadSubsidyReportDeclaration.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportDeclaration.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportDeclaration.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteSubsidyReportDeclaration.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportDeclaration.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(disbandReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(disbandReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(disbandReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(returnToAc.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(returnToAc.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(returnToAc.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(agreeReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(agreeReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(agreeReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportQuarterlyReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(uploadSubsidyReportQuarterlyReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportQuarterlyReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportQuarterlyReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteSubsidyReportQuarterlyReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportQuarterlyReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(acceptReportToPayment.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(acceptReportToPayment.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(acceptReportToPayment.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportReturnLetter.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(uploadSubsidyReportReturnLetter.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportReturnLetter.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(rejectReportByTreasury.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(rejectReportByTreasury.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(rejectReportByTreasury.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportPaymentDecision.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(uploadSubsidyReportPaymentDecision.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportPaymentDecision.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportUploadedPaymentDecision.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteSubsidyReportUploadedPaymentDecision.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportUploadedPaymentDecision.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportReturnDecision.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(uploadSubsidyReportReturnDecision.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(uploadSubsidyReportReturnDecision.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportUploadedReturnDecision.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteSubsidyReportUploadedReturnDecision.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(deleteSubsidyReportUploadedReturnDecision.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(rerunSubsidyReport.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(rerunSubsidyReport.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(rerunSubsidyReport.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            });
    }
});

export const { reset, startLoading, endLoading } = slice.actions;

const selectSubsidyReportCard = (state: AppState) => state.subsidyReportCard;

export const selectSubsidyReportCardStatus = (state: AppState) => selectSubsidyReportCard(state).status;

export const selectSubsidyReportCardIsLoading = (state: AppState) => selectSubsidyReportCardStatus(state) === 'loading';

export const selectSubsidyReport = (state: AppState) => selectSubsidyReportCard(state).data;

const selectSubsidyReportId = (state: AppState) => selectSubsidyReport(state)?.reportId;

export default slice.reducer;
